/**
 * ADR-0216 — support-login card on the account detail view.
 *
 * Superadmin only (the ops SPA hides the button for 'support'; the
 * server-side gate on POST /internal/admin/accounts/:id/support-login
 * is the load-bearing one).
 *
 * UX:
 *   - Empty state: one CTA "Generate link" + the constraints copy.
 *   - After mint: shows the URL in a monospaced code block with a
 *     Copy button, a live "expires in N min" countdown, and the
 *     reassurance / limits copy. If the mint revoked a prior grant
 *     (revoked_prior_count > 0) the card notes it.
 *   - Regenerate button: mints a fresh link; the previous one is
 *     revoked server-side (the "regenerating cancels" rule).
 *
 * Card copy anchors:
 *   "Single use. Generating a new link cancels any unused link. Read-only
 *    ends after 60 minutes."
 */

import { useEffect, useState } from 'react';

import type {
  AdminSupportLoginCreateResponse,
} from '@sill/types/api';

import { useAdminApi } from '../../api/api-context.js';
import { ApiError } from '../../api/http-client.js';
import { Card } from '../../components/atoms/Card.js';
import { useC } from '../../lib/theme.js';
import type { StaffRole } from '@sill/types/api';
import { hasRole } from '../../lib/roles.js';

const sansFont = "'Geist', sans-serif";
const monoFont = "'JetBrains Mono', monospace";

export interface SupportLoginCardProps {
  readonly accountId: string;
  readonly staffRole: StaffRole;
}

interface MintedLink {
  readonly url: string;
  readonly expires_at: string;
  readonly revoked_prior_count: number;
  allow_changes: boolean;
}

function useCountdown(expires_at: string | null): string {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (expires_at === null) return;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [expires_at]);
  if (expires_at === null) return '';
  const ms = new Date(expires_at).getTime() - now;
  if (ms <= 0) return 'expired';
  const mins = Math.floor(ms / 60_000);
  const secs = Math.floor((ms % 60_000) / 1000);
  return `${mins}m ${secs.toString().padStart(2, '0')}s`;
}

export function SupportLoginCard({
  accountId,
  staffRole,
}: SupportLoginCardProps): JSX.Element | null {
  const C = useC();
  const api = useAdminApi();
  const [minted, setMinted] = useState<MintedLink | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [allowChanges, setAllowChanges] = useState(false);

  const countdown = useCountdown(minted?.expires_at ?? null);

  // 'support' role sees nothing — the button doesn't render. The
  // server-side 403 is the belt; this is the suspenders.
  if (!hasRole(staffRole, 'support')) return null;
  const canAllowChanges = hasRole(staffRole, 'admin');

  async function generate(): Promise<void> {
    setBusy(true);
    setError(null);
    setCopied(false);
    try {
      const res: AdminSupportLoginCreateResponse = await api.createSupportLogin(
        accountId,
        allowChanges,
      );
      setMinted({
        url: res.url,
        expires_at: res.expires_at,
        revoked_prior_count: res.revoked_prior_count,
        allow_changes: res.allow_changes === true,
      });
    } catch (err) {
      if (err instanceof ApiError) {
        // The server maps 404 reason to a discriminator; surface a
        // useful message when available.
        if (err.status === 404) {
          setError('This account has no active owner user to impersonate.');
        } else if (err.status === 403) {
          setError('Superadmin role required.');
        } else if (err.status === 429) {
          setError('Rate limit reached — try again in a moment.');
        } else {
          setError('Could not mint a link. Try again.');
        }
      } else {
        setError('Could not mint a link. Try again.');
      }
    } finally {
      setBusy(false);
    }
  }

  async function copyLink(): Promise<void> {
    if (minted === null) return;
    try {
      await navigator.clipboard.writeText(minted.url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard permissions denied — no-op. The link remains
      // visible in the code block so the operator can select
      // manually.
    }
  }

  return (
    <Card>
      <div style={{ padding: 24 }}>
        <div
          style={{
            fontFamily: monoFont,
            fontSize: 10,
            letterSpacing: 2,
            color: C.muted,
            marginBottom: 8,
            textTransform: 'uppercase',
          }}
        >
          Support login
        </div>
        <p
          style={{
            fontFamily: sansFont,
            fontSize: 13,
            color: C.text,
            marginBottom: 16,
            lineHeight: 1.5,
          }}
        >
          Generate a one-time link to open this account as its owner.
          <br />
          <span style={{ color: C.muted }}>
            Single use, expires in 10 minutes. Generating a new link cancels any unused link for this
            account. The session ends after 60 minutes.
          </span>
        </p>
        {minted === null && canAllowChanges ? (
          <label
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'flex-start',
              fontFamily: sansFont,
              fontSize: 13,
              color: C.text,
              marginBottom: 14,
              lineHeight: 1.5,
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={allowChanges}
              onChange={(e) => setAllowChanges(e.target.checked)}
              style={{ marginTop: 3 }}
            />
            <span>
              Allow changes
              <span style={{ display: 'block', color: C.muted, fontSize: 12 }}>
                Off: read-only. On: you can change sites, policy, skills, catalog, overrides and
                integrations. Each change shows on the merchant&apos;s activity log as made by Sill support.
                Payments, refunds, billing, team, keys and deletion stay locked.
              </span>
            </span>
          </label>
        ) : null}
        {minted === null ? (
          <button
            type="button"
            onClick={() => void generate()}
            disabled={busy}
            style={{
              background: C.lime,
              color: C.ink,
              fontFamily: sansFont,
              fontWeight: 600,
              fontSize: 13,
              padding: '8px 16px',
              border: 'none',
              borderRadius: 2,
              cursor: busy ? 'wait' : 'pointer',
              opacity: busy ? 0.6 : 1,
            }}
          >
            {busy ? 'Generating…' : 'Generate link'}
          </button>
        ) : (
          <div>
            <div style={{ fontFamily: sansFont, fontSize: 12.5, color: minted.allow_changes ? C.amberFg ?? C.amber : C.muted, marginBottom: 8 }}>
              {minted.allow_changes ? 'Changes allowed (locked: payments, billing, team, keys, deletion).' : 'Read-only link.'}
            </div>
            <div
              style={{
                fontFamily: monoFont,
                fontSize: 11,
                background: C.surface2,
                border: `1px solid ${C.border}`,
                padding: 12,
                borderRadius: 2,
                marginBottom: 12,
                wordBreak: 'break-all',
                color: C.text,
              }}
            >
              {minted.url}
            </div>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <button
                type="button"
                onClick={() => void copyLink()}
                style={{
                  background: C.lime,
                  color: C.ink,
                  fontFamily: sansFont,
                  fontWeight: 600,
                  fontSize: 13,
                  padding: '6px 12px',
                  border: 'none',
                  borderRadius: 2,
                  cursor: 'pointer',
                }}
              >
                {copied ? 'Copied' : 'Copy link'}
              </button>
              <button
                type="button"
                onClick={() => void generate()}
                disabled={busy}
                style={{
                  background: 'transparent',
                  color: C.text,
                  fontFamily: sansFont,
                  fontSize: 13,
                  padding: '6px 12px',
                  border: `1px solid ${C.border}`,
                  borderRadius: 2,
                  cursor: busy ? 'wait' : 'pointer',
                }}
              >
                Regenerate
              </button>
              <span
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.muted,
                  letterSpacing: 1,
                  marginLeft: 'auto',
                  textTransform: 'uppercase',
                }}
              >
                Expires in {countdown}
              </span>
            </div>
            {minted.revoked_prior_count > 0 ? (
              <div
                style={{
                  fontFamily: monoFont,
                  fontSize: 10,
                  color: C.muted,
                  marginTop: 8,
                  letterSpacing: 1,
                  textTransform: 'uppercase',
                }}
              >
                Cancelled {minted.revoked_prior_count} previous link
                {minted.revoked_prior_count === 1 ? '' : 's'}.
              </div>
            ) : null}
          </div>
        )}
        {error !== null ? (
          <div
            style={{
              fontFamily: sansFont,
              fontSize: 12,
              color: C.coral,
              marginTop: 12,
            }}
          >
            {error}
          </div>
        ) : null}
      </div>
    </Card>
  );
}
